import { likeCheckIn, unlikeCheckIn } from '@/api/endpoints/activity';
import { useAuth } from '@/hooks/use-auth';
import type { FeedItem } from '@/types/feed';
import type { Paginated } from '@/types/pagination';
import { useMutation, useQueryClient, type InfiniteData, type QueryKey } from '@tanstack/react-query';
import { useCallback } from 'react';
import { FEED_QUERY_KEYS } from './use-activity';
import { applyOptimisticLike } from '../utils/optimistic-like';

export function useLikeCheckIn() {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();

  const mutation = useMutation({
    mutationFn: async ({
      checkInId,
      currentlyLiked,
    }: {
      checkInId: string;
      currentlyLiked: boolean;
    }) => {
      if (currentlyLiked) {
        return unlikeCheckIn(checkInId);
      }
      return likeCheckIn(checkInId);
    },

    onMutate: async ({ checkInId, currentlyLiked }) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: FEED_QUERY_KEYS.all });

      // Snapshot ALL feed queries for rollback
      const previousQueries = queryClient.getQueriesData<unknown>({
        queryKey: FEED_QUERY_KEYS.all,
      });

      // Helper to update a single FeedItem (shared optimistic recompute)
      const updateItem = (item: FeedItem): FeedItem => {
        if (item.checkIn.id !== checkInId) return item;
        return { ...item, ...applyOptimisticLike(item, currentUser, currentlyLiked) };
      };

      // Optimistically update ALL cached feed queries
      queryClient.setQueriesData<unknown>(
        { queryKey: FEED_QUERY_KEYS.all },
        (old: unknown) => {
          if (!old) return old;

          // Infinite query shape (activity, user, menu-item, business feeds)
          if (
            typeof old === 'object' &&
            'pages' in (old as any) &&
            Array.isArray((old as any).pages)
          ) {
            const data = old as InfiniteData<Paginated<FeedItem>>;
            return {
              ...data,
              pages: data.pages.map((page) => ({
                ...page,
                items: page.items.map(updateItem),
              })),
            };
          }

          // Single FeedItem shape (check-in detail)
          if (typeof old === 'object' && 'checkIn' in (old as any)) {
            return updateItem(old as FeedItem);
          }

          return old;
        },
      );

      return { previousQueries };
    },

    onError: (_error, _variables, context) => {
      // Rollback all feed queries from snapshot
      if (context?.previousQueries) {
        for (const [queryKey, data] of context.previousQueries as [QueryKey, unknown][]) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },

    onSettled: () => {
      // Refetch to ensure server state is in sync
      queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEYS.all });
    },
  });

  // Stable identity so memoized feed cards (ActivityCard) aren't invalidated
  // every render. `mutate` is referentially stable per React Query.
  const { mutate } = mutation;
  const toggleLike = useCallback(
    (checkInId: string, currentlyLiked: boolean) => {
      mutate({ checkInId, currentlyLiked });
    },
    [mutate],
  );

  return {
    toggleLike,
    isLiking: mutation.isPending,
  };
}
